import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Card, CardContent } from "@mui/material";
import { styled } from "@mui/system";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { FaSearch } from "react-icons/fa";
import { HiRefresh } from "react-icons/hi";
import Loader from "../component/Loader";
import Swal from "sweetalert2";
import { getData } from "../store/utils";
import { Pie, Bar } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js";
import "../css/makeTable.css";

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const StyledTableContainer = styled(TableContainer)({
    marginBottom: "20px",
    boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
    overflow: "hidden",
    
});

const StyledTable = styled(Table)({
    width: "100%",
    borderCollapse: "collapse",
    borderRadius: "10px",
    border: "1px solid #e0e0e0"
});

const StyledTableHead = styled(TableHead)({
    "& tr": {
        backgroundColor: "#f67555"
    }
});

const StyledTableBodyRow = styled(TableRow)({
    "&:nth-of-type(even)": {
        backgroundColor: "#fafafa"
    },
    "&:hover": {
        backgroundColor: "#f0f0f0"
    }
});

const OrderStatusDashboard = () => {
    const [orderData, setOrderData] = useState(null);
    const [fundData, setFundData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    useEffect(() => {
        fetchOrderData();
        fetchFundData();
    }, [fromDate, toDate]);

    const fetchOrderData = async () => {
        setIsLoading(true);
        try {
            let apiUrl = "orderReport/orderStatusData";
            if (fromDate || toDate) {
                const params = new URLSearchParams();
                if (fromDate) params.append("fromDate", format(new Date(fromDate), "yyyy-MM-dd"));
                if (toDate) params.append("toDate", format(new Date(toDate), "yyyy-MM-dd"));
                apiUrl += `?${params.toString()}`;
            }
            const response = await getData(apiUrl);
            if (response.statusCode === 200) {
                setOrderData(response.data || {});
            }
        } catch (error) {
            console.error("Error fetching order data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFundData = async () => {
        setIsLoading(true);
        try {
            let apiUrl = "orderReport/invoiceStatusData";
            if (fromDate || toDate) {
                const params = new URLSearchParams();
                if (fromDate) params.append("fromDate", format(new Date(fromDate), "yyyy-MM-dd"));
                if (toDate) params.append("toDate", format(new Date(toDate), "yyyy-MM-dd"));
                apiUrl += `?${params.toString()}`;
            }
            const response = await getData(apiUrl);
            if (response.statusCode === 200) {
                setFundData(response.data || {});
            }
        } catch (error) {
            console.error("Error fetching fund data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFromDateChange = (date) => setFromDate(date);
    const handleToDateChange = (date) => setToDate(date);
    const resetFilters = () => {
        setFromDate(null);
        setToDate(null);
        fetchOrderData();
        fetchFundData();
    };

    const allCategoriesOrder = Object.keys(orderData || {}).map(category => {
        const categoryTotalOrders = orderData[category].reduce((sum, item) => sum + item.total_orders, 0);
        const categoryTotalAmount = orderData[category].reduce((sum, item) => sum + item.total_order_amount, 0);
        return { category, categoryTotalOrders, categoryTotalAmount, items: orderData[category] };
    });

    const allCategoriesFund = Object.keys(fundData || {}).map(category => {
        const categoryTotalOrders = fundData[category].reduce((sum, item) => sum + item.total_orders, 0);
        const categoryTotalAmount = fundData[category].reduce((sum, item) => sum + item.total_order_amount, 0);
        return { category, categoryTotalOrders, categoryTotalAmount, items: fundData[category] };
    });

    const getPieChartData = (data) => {
        const labels = data.map(item => item.statusName);
        const totalOrders = data.map(item => item.total_orders);
        return {
            labels,
            datasets: [
                {
                    data: totalOrders,
                    backgroundColor: totalOrders.map(() => `#${Math.floor(Math.random() * 16777215).toString(16)}`),
                    borderColor: totalOrders.map(() => "#fff"),
                    borderWidth: 1,
                }
            ]
        };
    }      

    const STATUS_COLOR_MAP = {
        "Approved": "#b68d00",               // Dark Mustard
        "Duplicate": "#00876c",                // Dark Teal
        "Decline": "#b34700",                 // Dark Orange
        "Reject": "#b3003c",                  // Dark Red-Pink
        "Lender Approval Pending": "#4b3fbf", // Indigo
        "Pending But Not Processed": "#cc7354", // Burnt Salmon
        "Processing": "#0056b3",              // Navy Blue
        "Merchant Approval Pending": "#007c91", // Deep Cyan
        "Quotation Pending": "#b24872",       // Deep Pink
        "Order Disbursed": "#00724d",         // Dark Mint Green
        "Invoice Upload Pending": "#b33636",  // Dark Coral
        "Disbursement list not cleared": "#5e3a99", // Deep Purple
        "Disbursement Pending": "#992e40",    // Wine Red
        "Paid": "#208f5e",                    // Deep Green
        "Lender": "#b07e00",                  // Golden Brown
        "Pending": "#1e88e5"                  // Strong Sky Blue
    };
      
    const getBarChartData = (data) => {
        const labels = data.map(item => item.statusName);
        const totalOrders = data.map(item => item.total_orders);
        const backgroundColors = labels.map(label => STATUS_COLOR_MAP[label] || "#cccccc");

        return {
            labels,
            datasets: [
                {
                    label: "Total Orders",
                    data: totalOrders,
                    backgroundColor: backgroundColors
                }
            ]
        };
    };
    
    const chartOptions = {
        responsive: true,
        plugins: {
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || "";
                        const value = context.parsed.y;
                        const statusName = context.label;
                        return `${label}: ${value} (${statusName})`;
                    }
                }
            },
            legend: {
                display: false
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: "Status Name"
                }
            },
            y: {
                title: {
                    display: true,
                    text: "Total Orders"
                },
                beginAtZero: true
            }
        }
    };

    const orderPieData = allCategoriesOrder.flatMap(cat => cat.items);
    const fundPieData = allCategoriesFund.flatMap(cat => cat.items);

    return (
        <div className="dashboard-container">
            <div className='customHeaderTable'>
                <div className="container-fluid">
                    <div className="row alignmentTable">
                        <div className="col-md-3">
                            <label>From :</label>&nbsp;
                            <DatePicker className="form-control" dateFormat="dd-MM-yyyy" placeholderText="Select Start Date" selected={fromDate} onChange={handleFromDateChange} maxDate={new Date()} />
                        </div>
                        <div className="col-md-3">
                            <label>To :</label>&nbsp;
                            <DatePicker className="form-control" dateFormat="dd-MM-yyyy" placeholderText="Select end Date" selected={toDate} onChange={handleToDateChange} maxDate={new Date()} />
                        </div>
                        {/* <div className="col-md-2">
                            <button type="button" className="searchTableData" onClick={() => { fetchOrderData(); fetchFundData(); }}><FaSearch /> Search</button>
                        </div> */}
                        <div className="col-md-2 text-center" onClick={resetFilters}>
                            <button className="resetTableData"><HiRefresh /> Reset</button>
                        </div>
                    </div>
                </div>
            </div>
            {isLoading ? <Loader /> : (
                <>
                    <div className="mt-12">
                        <div className="centre">
                            <h4 style={{ textAlign: 'left' }}>Order Status</h4>
                        </div>
                        <Card>
                            <CardContent>
                                <div className="row statusTable">
                                    <div className="col-sm-6" style={{ height: '500px', overflow: 'auto'}} >
                                        <StyledTableContainer component={Paper}>
                                                <StyledTable>
                                                    <StyledTableHead>
                                                        <TableRow>
                                                            <TableCell style={{ color: '#fff' }}><b>Category</b></TableCell>
                                                            <TableCell style={{ color: '#fff' }}><b>Status Name</b></TableCell>
                                                            <TableCell style={{ color: '#fff' }}><b>Total Orders</b></TableCell>
                                                            <TableCell style={{ color: '#fff' }}><b>Total Order Amount</b></TableCell>
                                                        </TableRow>
                                                    </StyledTableHead>
                                                    <TableBody>
                                                        {allCategoriesOrder.map((cat, catIndex) => (
                                                            <React.Fragment key={catIndex}>
                                                                <TableRow>
                                                                    <TableCell><b>{cat.category}</b></TableCell>
                                                                    <TableCell></TableCell>
                                                                    <TableCell><b>{cat.categoryTotalOrders}</b></TableCell>
                                                                    <TableCell><b>{cat.categoryTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></TableCell>
                                                                </TableRow>
                                                                {cat.items.map((item, index) => (
                                                                    <StyledTableBodyRow key={index}>
                                                                        <TableCell></TableCell>
                                                                        <TableCell>{item.statusName}</TableCell>
                                                                        <TableCell>{item.total_orders}</TableCell>
                                                                        <TableCell>{item.total_order_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                                                    </StyledTableBodyRow>
                                                                ))}
                                                            </React.Fragment>
                                                        ))}
                                                        <TableRow>
                                                            <TableCell><b>Grand Total</b></TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell><b>{allCategoriesOrder.reduce((acc, cat) => acc + cat.categoryTotalOrders, 0)}</b></TableCell>
                                                            <TableCell><b>{allCategoriesOrder.reduce((acc, cat) => acc + cat.categoryTotalAmount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </StyledTable>
                                        </StyledTableContainer>
                                    </div>
                                    <div className="col-sm-6">
                                        <div style={{maxWidth:'100%', maxHeight:'100%', alignItems: 'center', justifyContent: 'center', display: 'flex'}}>
                                            <Bar data={getBarChartData(orderPieData)} options={chartOptions} />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <div className="mt-12">
                        <div className="centre">
                            <h4 style={{ textAlign: 'left', marginTop : '35px'}}>Fund Status</h4>
                        </div>
                        <Card>
                            <CardContent>
                                <div className="row statusTable">
                                    <div className="col-sm-6" style={{ height: '500px', overflow: 'auto'}}>
                                        <StyledTableContainer component={Paper}>
                                            <StyledTable>
                                                <StyledTableHead>
                                                    <TableRow>
                                                        <TableCell style={{ color: '#fff' }}><b>Category</b></TableCell>
                                                        <TableCell style={{ color: '#fff' }}><b>Status Name</b></TableCell>
                                                        <TableCell style={{ color: '#fff' }}><b>Total Orders</b></TableCell>
                                                        <TableCell style={{ color: '#fff' }}><b>Total Order Amount</b></TableCell>
                                                    </TableRow>
                                                </StyledTableHead>
                                                <TableBody>
                                                    {allCategoriesFund.map((cat, catIndex) => (
                                                        <React.Fragment key={catIndex}>
                                                            <TableRow>
                                                                <TableCell><b>{cat.category}</b></TableCell>
                                                                <TableCell></TableCell>
                                                                <TableCell><b>{cat.categoryTotalOrders}</b></TableCell>
                                                                <TableCell><b>{cat.categoryTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></TableCell>
                                                            </TableRow>
                                                            {cat.items.map((item, index) => (
                                                                <StyledTableBodyRow key={index}>
                                                                    <TableCell></TableCell>
                                                                    <TableCell>{item.statusName}</TableCell>
                                                                    <TableCell>{item.total_orders}</TableCell>
                                                                    <TableCell>{item.total_order_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                                                </StyledTableBodyRow>
                                                            ))}
                                                        </React.Fragment>
                                                    ))}
                                                    <TableRow>
                                                        <TableCell><b>Grand Total</b></TableCell>
                                                        <TableCell></TableCell>
                                                        <TableCell><b>{allCategoriesFund.reduce((acc, cat) => acc + cat.categoryTotalOrders, 0)}</b></TableCell>
                                                        <TableCell><b>{allCategoriesFund.reduce((acc, cat) => acc + cat.categoryTotalAmount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </StyledTable>
                                        </StyledTableContainer>
                                    </div>
                                    <div className="col-sm-6">
                                        <div style={{maxWidth:'87%', maxHeight:'87%', alignItems: 'center', justifyContent: 'center', display: 'flex', marginLeft: '65px'}}>
                                            <Bar data={getBarChartData(fundPieData)} options={chartOptions} />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
};

export default OrderStatusDashboard;